import React, { useEffect, useState } from 'react';
import {
  Paper, Table, TableHead, TableRow, TableCell, Button, TableBody, Typography, Box,
  IconButton, TextField, Grid, useMediaQuery, useTheme, CircularProgress, Snackbar,
  Alert, TableContainer, TablePagination, InputAdornment, Chip, Checkbox, ListItemText,
  Popover, List, ListItem, ListItemButton, Divider, TableSortLabel
} from '@mui/material';
import {
  PictureAsPdf as PictureAsPdfIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const EntradasMercancia = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [data, setData] = useState([]); // siempre array
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({ rfcs: [], partners: [], fechaDesde: '', fechaHasta: '' });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const [usersData, setUsersData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentFilter, setCurrentFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('MaterialDocument');

  const [currentUser, setCurrentUser] = useState(null);
  const [userRfc, setUserRfc] = useState('');
  const [userPartner, setUserPartner] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      try {
        setLoading(true);

        // Usuario actual
        const userResponse = await axios.get('http://172.20.0.11:5000/api/validate-session', {
          withCredentials: true, signal: controller.signal
        });
        setCurrentUser(userResponse.data.user);
        setUserRfc(userResponse.data.user.rfc || userResponse.data.user.username);

        // Catálogo usuarios
        const usersResponse = await axios.get('http://172.20.0.11:5000/api/usuarios', {
          withCredentials: true, signal: controller.signal
        });
        const currentUserData =
          usersResponse.data.find(u => u.id === userResponse.data.user.id) ||
          usersResponse.data.find(u => (u.rfc || u.username) === (userResponse.data.user.rfc || userResponse.data.user.username));
        setUserPartner(currentUserData?.partner || '');

        // Datos de Entradas (backend devuelve array; soportamos también {rows})
        const response = await axios.get('http://172.20.0.11:5000/api/entradas-mercancia', {
          withCredentials: true, signal: controller.signal
        });
        const rows = Array.isArray(response.data?.rows)
          ? response.data.rows
          : Array.isArray(response.data)
          ? response.data
          : [];

        setUsersData(Array.isArray(usersResponse.data) ? usersResponse.data : []);
        setData(rows);
        setFilteredData(rows);
      } catch (err) {
        if (axios.isCancel(err)) return;
        console.error('Error al cargar datos de entradas mercancia:', err);
        setError('Error al cargar los datos de entradas de mercancía. Intente nuevamente.');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => controller.abort();
  }, []);

  // Helpers catálogo
  const getUserNameByPartner = (partnerId) => {
    if (!partnerId || !Array.isArray(usersData) || usersData.length === 0) return '';
    const cleanPartner = String(partnerId).replace(/^0+/, '');
    const user = usersData.find(u => u.partner && String(u.partner).replace(/^0+/, '') === cleanPartner);
    return user ? user.nombre || '' : '';
  };

  const getRfcByPartner = (partnerId) => {
    if (!partnerId || !Array.isArray(usersData) || usersData.length === 0) return '';
    const cleanPartner = String(partnerId).replace(/^0+/, '');
    const user = usersData.find(usr => usr.partner && String(usr.partner).replace(/^0+/, '') === cleanPartner);
    return user ? (user.username || user.rfc || '') : '';
  };

  // === Preferir datos del servidor y hacer fallback al catálogo ===
  const getRFC = (row) => row.rfc || getRfcByPartner(row.partner_id) || '';
  const getNombre = (row) => row.nombre || getUserNameByPartner(row.partner_id) || '';
  const getPartnerIdStr = (row) => (row.partner_id != null ? String(row.partner_id) : '');

  const getUniqueRfcsInData = () => {
    const isProveedor = currentUser?.roles?.includes('Proveedor');
    if (isProveedor) return [userRfc];
    if (!Array.isArray(data)) return [];
    const rfcsInData = data.map(r => getRFC(r)).filter(Boolean);
    return [...new Set(rfcsInData)];
  };

  const getUniquePartnersInData = () => {
    const isProveedor = currentUser?.roles?.includes('Proveedor');
    if (isProveedor) return [userPartner];
    if (!Array.isArray(data)) return [];
    const partners = data.map(r => getPartnerIdStr(r)).filter(Boolean);
    return [...new Set(partners)];
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getComparableValue = (row, by) => {
    switch (by) {
      case 'rfc':
        return getRFC(row).toLowerCase();
      case 'nombre':
        return getNombre(row).toLowerCase();
      case 'fecha_creacion':
        return row.fecha_creacion ? new Date(row.fecha_creacion).getTime() : 0;
      case 'partner_id':
        return getPartnerIdStr(row);
      case 'MaterialDocument':
        return Number(row.MaterialDocument) || 0;
      default:
        return (row?.[by] ?? '').toString().toLowerCase();
    }
  };

  const descendingComparator = (a, b, by) => {
    const valA = getComparableValue(a, by);
    const valB = getComparableValue(b, by);
    if (valB < valA) return -1;
    if (valB > valA) return 1;
    return 0;
  };

  const getComparator = (ord, by) =>
    ord === 'desc' ? (a, b) => descendingComparator(a, b, by) : (a, b) => -descendingComparator(a, b, by);

  useEffect(() => {
    const base = Array.isArray(data) ? data : [];
    const filtered = base.filter(item => {
      const s = searchTerm.toLowerCase();

      const matchesSearch =
        String(item.MaterialDocument ?? '').toLowerCase().includes(s) ||
        getRFC(item).toLowerCase().includes(s) ||
        getPartnerIdStr(item).toLowerCase().includes(s) ||
        getNombre(item).toLowerCase().includes(s) ||
        (item.fecha_creacion
          ? new Date(item.fecha_creacion).toLocaleDateString('es-MX').toLowerCase().includes(s)
          : false);

      const matchesRfc = filters.rfcs.length > 0 ? filters.rfcs.includes(getRFC(item)) : true;

      const matchesPartner = filters.partners.length > 0
        ? filters.partners.includes(getPartnerIdStr(item))
        : true;

      const fechaItem = item.fecha_creacion ? new Date(item.fecha_creacion) : null;
      const matchesFechaDesde = filters.fechaDesde
        ? (fechaItem ? fechaItem >= new Date(filters.fechaDesde) : false)
        : true;
      const matchesFechaHasta = filters.fechaHasta
        ? (fechaItem ? fechaItem <= new Date(filters.fechaHasta + 'T23:59:59') : false)
        : true;

      const isProveedor = currentUser?.roles?.includes('Proveedor');
      const matchesProveedorRfc = isProveedor ? getRFC(item) === userRfc : true;

      return (
        matchesSearch &&
        matchesRfc &&
        matchesPartner &&
        matchesFechaDesde &&
        matchesFechaHasta &&
        matchesProveedorRfc
      );
    });

    const sortedData = filtered.sort(getComparator(order, orderBy));
    setFilteredData(sortedData);
    setPage(0);
  }, [data, searchTerm, filters, usersData, order, orderBy, currentUser, userRfc]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const verPDF = (MaterialDocument) => {
    try {
      const url = `http://172.20.0.11:5000/api/entradas-mercancia/${MaterialDocument}/pdf`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error al abrir PDF:', error);
      setError('Error al visualizar el PDF');
      setSnackbarOpen(true);
    }
  };

  const handleBulkDownload = async (type) => {
    try {
      setLoading(true);
      if (selectedRows.length === 0) {
        setError('Selecciona al menos un registro para descargar');
        setSnackbarOpen(true);
        return;
      }
      const url = `http://172.20.0.11:5000/api/carta-porte/download-${type}?ids=${selectedRows.join(',')}`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error al descargar archivos:', error);
      setError('Error al generar archivo de descarga. Por favor intente nuevamente.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({ rfcs: [], partners: [], fechaDesde: '', fechaHasta: '' });
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = filteredData.map((row) => row.MaterialDocument);
      setSelectedRows(newSelected);
      setRowSelectionModel(newSelected);
      return;
    }
    setSelectedRows([]);
    setRowSelectionModel([]);
  };

  const handleRowClick = (event, MaterialDocument) => {
    const selectedIndex = selectedRows.indexOf(MaterialDocument);
    let newSelected = [];

    if (selectedIndex === -1) newSelected = newSelected.concat(selectedRows, MaterialDocument);
    else if (selectedIndex === 0) newSelected = newSelected.concat(selectedRows.slice(1));
    else if (selectedIndex === selectedRows.length - 1) newSelected = newSelected.concat(selectedRows.slice(0, -1));
    else if (selectedIndex > 0)
      newSelected = newSelected.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1)
      );

    setSelectedRows(newSelected);
    setRowSelectionModel(newSelected);
  };

  const isSelected = (MaterialDocument) => selectedRows.indexOf(MaterialDocument) !== -1;

  const handleFilterClick = (event, filterType) => {
    setCurrentFilter(filterType);
    setAnchorEl(event.currentTarget);
    setFilterSearch('');
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
    setCurrentFilter(null);
    setFilterSearch('');
  };

  const handleToggleFilterItem = (value) => {
    const filterKey = currentFilter === 'rfc' ? 'rfcs' : 'partners';
    const currentFilters = [...filters[filterKey]];
    const currentIndex = currentFilters.indexOf(value);

    if (currentIndex === -1) currentFilters.push(value);
    else currentFilters.splice(currentIndex, 1);

    setFilters({ ...filters, [filterKey]: currentFilters });
  };

  const handleSelectAllFilterItems = () => {
    const filterKey = currentFilter === 'rfc' ? 'rfcs' : 'partners';
    const allValues = currentFilter === 'rfc'
      ? getUniqueRfcsInData()
      : getUniquePartnersInData().map(String);

    setFilters({
      ...filters,
      [filterKey]: filters[filterKey].length === allValues.length ? [] : allValues
    });
  };

  const getFilteredOptions = () => {
    if (currentFilter === 'rfc') {
      return getUniqueRfcsInData().filter(rfc =>
        rfc.toLowerCase().includes(filterSearch.toLowerCase())
      );
    } else if (currentFilter === 'partner') {
      return getUniquePartnersInData()
        .map(String)
        .filter(partner =>
          partner.toLowerCase().includes(filterSearch.toLowerCase()) ||
          getNombre({ partner_id: partner }).toLowerCase().includes(filterSearch.toLowerCase())
        );
    }
    return [];
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? 'filter-popover' : undefined;
  const isProveedor = currentUser?.roles?.includes('Proveedor');

  return (
    <Box sx={{ p: isMobile ? 1 : 2, maxWidth: '1200px', margin: '0 auto' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          mb: 2,
          gap: 1
        }}
      >
        <Typography variant="h6" component="h1" sx={{ fontWeight: 'bold' }}>
          Entradas de Mercancía
        </Typography>

        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate('/')}
          startIcon={<ArrowBackIcon fontSize="small" />}
          sx={{ width: isMobile ? '100%' : 'auto', fontSize: '0.8rem' }}
        >
          Volver a factura
        </Button>
      </Box>

      <Paper
        elevation={1}
        sx={{
          p: 2, mb: 1.5, borderRadius: 3, backgroundColor: '#fff',
          boxShadow: '0 10px 24px rgba(2, 6, 23, 0.06)', border: '1px solid #eef2f7'
        }}
      >
        <Grid container spacing={1}>
          <Grid item xs={12} sm={5} md={3}>
            <TextField
              fullWidth size="small" label="Buscar" variant="outlined"
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
                endAdornment: searchTerm && (
                  <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ mr: -1 }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ),
                sx: { height: '35px' }
              }}
              sx={{
                '& .MuiOutlinedInput-root': { height: '35px' },
                '& .MuiInputLabel-outlined': { transform: 'translate(14px, 14px) scale(1)' },
                '& .MuiInputLabel-outlined.MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.75)' }
              }}
            />
          </Grid>

          {!isProveedor && (
            <>
              <Grid item xs={6} sm={3} md={2}>
                <Button
                  fullWidth variant="outlined" size="small"
                  onClick={(e) => handleFilterClick(e, 'rfc')}
                  sx={{ height: '35px', justifyContent: 'space-between', textTransform: 'none' }}
                  endIcon={<FilterListIcon fontSize="small" />}
                >
                  RFC Emisor
                  {filters.rfcs.length > 0 && <Chip label={filters.rfcs.length} size="small" sx={{ ml: 1 }} />}
                </Button>
              </Grid>

              <Grid item xs={6} sm={3} md={2}>
                <Button
                  fullWidth variant="outlined" size="small"
                  onClick={(e) => handleFilterClick(e, 'partner')}
                  sx={{ height: '35px', justifyContent: 'space-between', textTransform: 'none' }}
                  endIcon={<FilterListIcon fontSize="small" />}
                >
                  Partner
                  {filters.partners.length > 0 && <Chip label={filters.partners.length} size="small" sx={{ ml: 1 }} />}
                </Button>
              </Grid>
            </>
          )}

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth size="small" label="Fecha desde" type="date" InputLabelProps={{ shrink: true }}
              value={filters.fechaDesde}
              onChange={(e) => setFilters({ ...filters, fechaDesde: e.target.value })}
              sx={{ height: '35px', '& .MuiInputBase-root': { height: '35px' }, '& .MuiInputBase-input': { fontSize: '0.8rem', padding: '8.5px 14px' } }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth size="small" label="Fecha hasta" type="date" InputLabelProps={{ shrink: true }}
              value={filters.fechaHasta}
              onChange={(e) => setFilters({ ...filters, fechaHasta: e.target.value })}
              sx={{ height: '35px', '& .MuiInputBase-root': { height: '35px' }, '& .MuiInputBase-input': { fontSize: '0.8rem', padding: '8.5px 14px' } }}
            />
          </Grid>

          <Grid item xs={6} sm={2} md={1}>
            <Button
              fullWidth size="small" variant="outlined" color="error"
              onClick={handleClearFilters} startIcon={<ClearIcon fontSize="small" />}
              sx={{ height: '35px', fontSize: '0.7rem' }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Popover
        id={popoverId} open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { width: 300, maxHeight: 400, p: 1 } }}
      >
        <TextField
          fullWidth size="small"
          placeholder={`Buscar ${currentFilter === 'rfc' ? 'RFC' : 'Partner'}...`}
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
          sx={{ mb: 1 }}
        />

        <List dense sx={{ width: '100%', maxHeight: 300, overflow: 'auto' }}>
          {!isProveedor && (
            <>
              <ListItemButton onClick={handleSelectAllFilterItems}>
                <Checkbox
                  edge="start"
                  checked={
                    currentFilter === 'rfc'
                      ? filters.rfcs.length === getUniqueRfcsInData().length
                      : filters.partners.length === getUniquePartnersInData().length
                  }
                  indeterminate={
                    currentFilter === 'rfc'
                      ? filters.rfcs.length > 0 && filters.rfcs.length < getUniqueRfcsInData().length
                      : filters.partners.length > 0 && filters.partners.length < getUniquePartnersInData().length
                  }
                  tabIndex={-1}
                  disableRipple
                />
                <ListItemText primary="Seleccionar todos" />
              </ListItemButton>
              <Divider />
            </>
          )}

          {(() => {
            const options =
              currentFilter === 'rfc'
                ? getUniqueRfcsInData().filter(r => r.toLowerCase().includes(filterSearch.toLowerCase()))
                : currentFilter === 'partner'
                ? getUniquePartnersInData()
                    .map(String)
                    .filter(p =>
                      p.toLowerCase().includes(filterSearch.toLowerCase()) ||
                      getNombre({ partner_id: p }).toLowerCase().includes(filterSearch.toLowerCase())
                    )
                : [];

            return options.map((value) => {
              const labelId = `checkbox-list-label-${value}`;
              const selected = currentFilter === 'rfc'
                ? filters.rfcs.includes(value)
                : filters.partners.includes(value);

              return (
                <ListItem key={value} disablePadding>
                  <ListItemButton role={undefined} onClick={() => handleToggleFilterItem(value)} dense>
                    <Checkbox edge="start" checked={selected} tabIndex={-1} disableRipple inputProps={{ 'aria-labelledby': labelId }} />
                    <ListItemText
                      id={labelId}
                      primary={value}
                      secondary={currentFilter === 'partner' ? getNombre({ partner_id: value }) : null}
                      sx={{ '& .MuiListItemText-secondary': { fontSize: '0.75rem', color: 'text.secondary' } }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            });
          })()}
        </List>
      </Popover>

      {selectedRows.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexDirection: isMobile ? 'column' : 'row' }}>
          <Button
            variant="outlined" size="small" color="error" startIcon={<PictureAsPdfIcon />}
            onClick={() => handleBulkDownload('pdf')} disabled={loading} sx={{ fontSize: '0.75rem' }}
          >
            Descargar PDF ({selectedRows.length})
          </Button>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : filteredData.length === 0 ? (
        <Paper elevation={1} sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Typography variant="body2">
            {searchTerm ||
            Object.values(filters).some(arr => (Array.isArray(arr) ? arr.length > 0 : !!arr)) ||
            filters.fechaDesde || filters.fechaHasta
              ? 'No se encontraron resultados'
              : 'No hay registros disponibles'}
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer
            component={Paper} elevation={1}
            sx={{
              maxHeight: '500px', overflow: 'auto',
              '&::-webkit-scrollbar': { width: '6px', height: '6px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#bdbdbd', borderRadius: '3px' }
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: '#f3f4f6',
                    '& th': { fontWeight: 'bold', fontSize: '0.75rem', py: 1, borderBottom: '1px solid #bdbdbd' }
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={selectedRows.length > 0 && selectedRows.length < filteredData.length}
                      checked={filteredData.length > 0 && selectedRows.length === filteredData.length}
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'MaterialDocument'}
                      direction={orderBy === 'MaterialDocument' ? order : 'asc'}
                      onClick={() => handleRequestSort('MaterialDocument')}
                    >
                      Material
                    </TableSortLabel>
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'rfc'}
                          direction={orderBy === 'rfc' ? order : 'asc'}
                          onClick={() => handleRequestSort('rfc')}
                        >
                          RFC Emisor
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'partner_id'}
                          direction={orderBy === 'partner_id' ? order : 'asc'}
                          onClick={() => handleRequestSort('partner_id')}
                        >
                          Partner
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'nombre'}
                          direction={orderBy === 'nombre' ? order : 'asc'}
                          onClick={() => handleRequestSort('nombre')}
                        >
                          Nombre
                        </TableSortLabel>
                      </TableCell>
                    </>
                  )}
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'fecha_creacion'}
                      direction={orderBy === 'fecha_creacion' ? order : 'asc'}
                      onClick={() => handleRequestSort('fecha_creacion')}
                    >
                      Fecha Creación
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    const isItemSelected = isSelected(row.MaterialDocument);
                    return (
                      <TableRow
                        key={row.MaterialDocument}
                        hover
                        onClick={(event) => handleRowClick(event, row.MaterialDocument)}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        selected={isItemSelected}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#f5f5f5' },
                          '& td': { fontSize: '0.75rem', py: 0.5 }
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox color="primary" checked={isItemSelected} />
                        </TableCell>
                        <TableCell>{row.MaterialDocument}</TableCell>

                        {!isMobile && (
                          <>
                            <TableCell>
                              <Chip
                                label={getRFC(row)}
                                size="small"
                                sx={{ fontSize: '0.7rem', height: '24px' }}
                              />
                            </TableCell>
                            <TableCell>{getPartnerIdStr(row)}</TableCell>
                            <TableCell>{getNombre(row)}</TableCell>
                          </>
                        )}

                        <TableCell>
                          {row.fecha_creacion
                            ? new Date(row.fecha_creacion).toLocaleDateString('es-MX', {
                                day: '2-digit', month: '2-digit', year: 'numeric'
                              })
                            : ''}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                verPDF(row.MaterialDocument);
                              }}
                              aria-label="Ver PDF"
                              color="error"
                              sx={{ p: 0.5, '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' } }}
                            >
                              <PictureAsPdfIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              mt: 1,
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' }
            }}
          />
        </>
      )}

      <Snackbar
        open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%', fontSize: '0.8rem' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EntradasMercancia;
